import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  priceConfigsApi,
  priceImportsApi,
  sellersApi,
  type PriceConfig,
  type PreviewResponse,
  type CommitResponse,
  type PriceImport,
  type SellerListItem,
  KNOWN_PRICE_FIELDS,
} from '../lib/api';
import { useAuth } from '../lib/auth';

export default function PriceImports() {
  const { user } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);

  const [sellers, setSellers] = useState<SellerListItem[]>([]);
  const [configs, setConfigs] = useState<PriceConfig[]>([]);
  const [history, setHistory] = useState<PriceImport[]>([]);

  const [sellerId, setSellerId] = useState<number | ''>('');
  const [configId, setConfigId] = useState<number | ''>('');
  const [file, setFile] = useState<File | null>(null);
  const [overwrite, setOverwrite] = useState(true);

  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [committed, setCommitted] = useState<CommitResponse | null>(null);
  const [busy, setBusy] = useState<'preview' | 'commit' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (user?.role === 'admin') sellersApi.list().then((r) => setSellers(r.sellers)).catch(() => { /* noop */ });
  }, [user]);

  useEffect(() => {
    priceConfigsApi.list(typeof sellerId === 'number' ? sellerId : undefined)
      .then((r) => {
        setConfigs(r.configs);
        if (r.configs.length === 1) setConfigId(r.configs[0].id);
        const dflt = r.configs.find((c) => c.is_default);
        if (dflt && !configId) setConfigId(dflt.id);
      })
      .catch(() => setConfigs([]));
  }, [sellerId]);  // eslint-disable-line react-hooks/exhaustive-deps

  function reloadHistory() {
    priceImportsApi.list(typeof sellerId === 'number' ? sellerId : undefined)
      .then((r) => setHistory(r.imports))
      .catch(() => setHistory([]));
  }
  useEffect(() => { reloadHistory(); }, [sellerId]);  // eslint-disable-line react-hooks/exhaustive-deps

  async function onPreview() {
    if (!file || !configId) { setErr('Выбери файл и шаблон'); return; }
    setBusy('preview');
    setErr(null);
    setCommitted(null);
    try {
      const r = await priceImportsApi.preview(file, configId, 20);
      setPreview(r);
    } catch (e) {
      setErr(String((e as Error)?.message ?? e));
    } finally { setBusy(null); }
  }

  async function onCommit() {
    if (!file || !configId) { setErr('Выбери файл и шаблон'); return; }
    if (!confirm(`Залить прайс? Это применит изменения к товарам продавца.\n\nЗатронуто строк: ${preview?.total_rows ?? '?'}.`)) return;
    setBusy('commit');
    setErr(null);
    try {
      const r = await priceImportsApi.commit(file, configId, overwrite);
      setCommitted(r);
      reloadHistory();
      // Reset preview/file so user doesn't accidentally re-commit.
      setPreview(null);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      setErr(String((e as Error)?.message ?? e));
    } finally { setBusy(null); }
  }

  function sellerLabel(id: number) {
    const s = sellers.find((x) => x.id === id);
    return s ? (s.display_name || s.login) : `#${id}`;
  }
  function configLabel(id: number | null) {
    if (id == null) return <span className="muted">—</span>;
    const c = configs.find((x) => x.id === id);
    return c ? c.name : `#${id}`;
  }

  return (
    <div className="container">
      <div className="page-head">
        <h2>Загрузка прайса</h2>
        <div className="row">
          <Link to="/prices/configs" className="btn">→ Шаблоны</Link>
        </div>
      </div>

      <div className="card form">
        <div className="row" style={{ gap: 12, alignItems: 'flex-end' }}>
          {user?.role === 'admin' && (
            <label style={{ flex: 1 }}>
              <span>Продавец</span>
              <select
                value={sellerId}
                onChange={(e) => { setSellerId(e.target.value ? Number(e.target.value) : ''); setConfigId(''); setPreview(null); }}
              >
                <option value="">— выберите —</option>
                {sellers.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.login}</option>)}
              </select>
            </label>
          )}
          <label style={{ flex: 1 }}>
            <span>Шаблон</span>
            <select value={configId} onChange={(e) => { setConfigId(e.target.value ? Number(e.target.value) : ''); setPreview(null); }}>
              <option value="">— выберите —</option>
              {configs.map((c) => (
                <option key={c.id} value={c.id}>{c.name}{c.is_default ? ' (по умолч.)' : ''}</option>
              ))}
            </select>
          </label>
          <label style={{ flex: 2 }}>
            <span>Файл .xlsx</span>
            <input ref={fileRef} type="file" accept=".xlsx" onChange={(e) => { setFile(e.target.files?.[0] ?? null); setPreview(null); setCommitted(null); }} />
          </label>
        </div>

        <label className="row">
          <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
          <span>Обновлять существующие товары (по артикулу). Если выключено — пропускать.</span>
        </label>

        <div className="row">
          <button className="btn" disabled={busy !== null} onClick={onPreview}>
            {busy === 'preview' ? 'Анализ…' : 'Просмотреть'}
          </button>
          <button className="btn primary" disabled={busy !== null || !preview} onClick={onCommit}>
            {busy === 'commit' ? 'Заливаем…' : 'Залить'}
          </button>
        </div>

        {err && <div className="error">{err}</div>}
      </div>

      {preview && !committed && (
        <div className="card sub" style={{ marginTop: 18 }}>
          <h3>Превью</h3>
          <p className="muted">
            Лист <code>{preview.sheetName}</code>, всего строк: {preview.total_rows}. Показано {preview.preview.length}.
          </p>
          <p>
            <strong>Маппинг:</strong>{' '}
            {Object.entries(preview.colMap).map(([f, c]) => (
              <span key={f} className="badge ok" style={{ marginRight: 6 }}>{f} ← col {c}</span>
            ))}
            {Object.entries(preview.colMap).length === 0 && (
              <span className="badge warn">Ни одно поле не сопоставилось — проверь шаблон</span>
            )}
          </p>

          {preview.duplicates && preview.duplicates.length > 0 && (
            <div className="error" style={{ marginBottom: 14 }}>
              <strong>В файле есть дублирующиеся артикулы ({preview.duplicates.length} групп).</strong>{' '}
              Если зальёшь как есть — для каждого дубля последняя строка перезапишет предыдущие
              (товары станут одним). Лучше исправить SKU в Excel у продавца, либо переименовать
              позже в карточке товара после импорта.
              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: 'pointer' }}>Показать список ({preview.duplicates.length})</summary>
                <table className="grid compact" style={{ marginTop: 8 }}>
                  <thead>
                    <tr><th>SKU</th><th>Строк</th><th>Названия</th></tr>
                  </thead>
                  <tbody>
                    {preview.duplicates.map((d) => (
                      <tr key={d.sku}>
                        <td><code>{d.sku}</code></td>
                        <td>{d.count}</td>
                        <td>{d.rows.map((r) => `${r.row}: ${r.name ?? '—'}`).join(' • ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            </div>
          )}
          {preview.preview.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table className="grid compact">
                <thead>
                  <tr>
                    <th>#</th>
                    {KNOWN_PRICE_FIELDS.filter((f) => preview.preview.some((r) => r.mapped[f] != null)).map((f) => (
                      <th key={f}>{f}</th>
                    ))}
                    <th>Ошибки</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.map((r) => (
                    <tr key={r.rowNum}>
                      <td>{r.rowNum}</td>
                      {KNOWN_PRICE_FIELDS.filter((f) => preview.preview.some((x) => x.mapped[f] != null)).map((f) => (
                        <td key={f}>{r.mapped[f] != null ? String(r.mapped[f]) : <span className="muted">—</span>}</td>
                      ))}
                      <td>{r.errors.length ? <span className="badge warn">{r.errors.join('; ')}</span> : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {committed && (
        <div className="card sub" style={{ marginTop: 18 }}>
          <h3>Залито</h3>
          <p>
            Создано: <strong>{committed.inserted}</strong>{' · '}
            Обновлено: <strong>{committed.updated}</strong>{' · '}
            Пропущено: <strong>{committed.skipped}</strong>{' из '}{committed.total_rows}
          </p>
          {committed.errors.length > 0 && (
            <details>
              <summary>{committed.errors.length} ошибок при импорте</summary>
              <table className="grid compact">
                <thead><tr><th>Строка</th><th>Ошибка</th></tr></thead>
                <tbody>
                  {committed.errors.map((e, i) => (
                    <tr key={i}><td>{e.row}</td><td>{e.error}</td></tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
          <p>
            <Link to={`/products?seller_id=${configs.find((c) => c.id === configId)?.seller_id}`}>→ К товарам</Link>
          </p>
        </div>
      )}

      <h3 style={{ marginTop: 28 }}>История импортов</h3>
      {history.length === 0 ? (
        <p className="muted">Пока пусто.</p>
      ) : (
        <table className="grid compact">
          <thead>
            <tr>
              <th>#</th>
              <th>Дата</th>
              {user?.role === 'admin' && <th>Продавец</th>}
              <th>Шаблон</th>
              <th>Файл</th>
              <th>Статус</th>
              <th>Создано</th>
              <th>Обновлено</th>
              <th>Пропущено</th>
              <th>Ошибки</th>
            </tr>
          </thead>
          <tbody>
            {history.map((h) => (
              <tr key={h.id}>
                <td>{h.id}</td>
                <td>{h.uploaded_at?.slice(0, 19)}</td>
                {user?.role === 'admin' && <td>{sellerLabel(h.seller_id)}</td>}
                <td>{configLabel(h.config_id)}</td>
                <td>{h.original_filename ?? '—'}</td>
                <td><span className={`badge ${h.status === 'success' ? 'ok' : 'warn'}`}>{h.status}</span></td>
                <td>{h.rows_inserted}</td>
                <td>{h.rows_updated}</td>
                <td>{h.rows_skipped}</td>
                <td>{h.errors?.length ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
